import React, { useState } from 'react';
import type { Socket } from 'socket.io-client';

interface OnlinePlayer {
  id: string;
  name: string;
  level: number;
  pieces: number;
  position: { x: number; y: number };
  hp: string;
  combatState: string;
  shipType: string;
}

interface AdminPanelPlayersTabProps {
  apiBaseUrl: string;
  socket?: Socket | null;
  playerId?: string;
  isBusy: boolean;
  onStateChange: (state: { isBusy: boolean; lastMessage: string | null }) => void;
}

export const AdminPanelPlayersTab: React.FC<AdminPanelPlayersTabProps> = ({
  apiBaseUrl,
  socket,
  playerId,
  isBusy,
  onStateChange,
}) => {
  const [onlinePlayers, setOnlinePlayers] = useState<OnlinePlayer[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  const [selectedShipType, setSelectedShipType] = useState<string>('sloop');

  const loadOnlinePlayers = async (): Promise<void> => {
    onStateChange({ isBusy: true, lastMessage: 'Loading online players...' });
    try {
      const res = await fetch(`${apiBaseUrl}/api/admin/dev/online-players`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setOnlinePlayers(data.players || []);
      onStateChange({ isBusy: false, lastMessage: `Found ${data.players?.length || 0} players` });
    } catch (err) {
      onStateChange({
        isBusy: false,
        lastMessage: `Error: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  const callAdminEndpoint = async (
    endpoint: string,
    actionName: string,
    data?: Record<string, any>
  ): Promise<void> => {
    onStateChange({ isBusy: true, lastMessage: `${actionName}...` });
    try {
      const res = await fetch(`${apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data || {}),
      });
      const text: string = await res.text();
      const responseData = (() => {
        try {
          return JSON.parse(text);
        } catch {
          return null;
        }
      })();
      if (!res.ok) {
        const message: string =
          typeof responseData?.error === 'string'
            ? responseData.error
            : `HTTP ${res.status}: ${text.slice(0, 200)}`;
        throw new Error(message);
      }
      onStateChange({
        isBusy: false,
        lastMessage: responseData?.message || `${actionName} completed`,
      });
    } catch (err) {
      onStateChange({
        isBusy: false,
        lastMessage: `Error: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  const teleportToTile = (playerId: string, tileX: number, tileY: number): void => {
    void callAdminEndpoint('/api/admin/dev/teleport-to-tile', 'Teleport', {
      playerId,
      tileX,
      tileY,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Load Online Players */}
      <button
        type="button"
        onClick={loadOnlinePlayers}
        disabled={isBusy}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#2d3748',
          color: '#edf2f7',
          border: '1px solid #4a5568',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
          width: '100%',
        }}
      >
        Load Online Players
      </button>

      {/* Players List */}
      {onlinePlayers.length > 0 && (
        <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827', maxHeight: 200, overflowY: 'auto' }}>
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10 }}>Online Players ({onlinePlayers.length})</div>
          {onlinePlayers.map((player) => (
            <div
              key={player.id}
              onClick={() => setSelectedPlayerId(player.id)}
              style={{
                padding: '4px',
                marginBottom: '4px',
                backgroundColor: selectedPlayerId === player.id ? '#2d3748' : '#1f2933',
                border: `1px solid ${selectedPlayerId === player.id ? '#4a5568' : '#2d3748'}`,
                cursor: 'pointer',
                fontSize: 9,
              }}
            >
              <div style={{ fontWeight: 600 }}>{player.name} (Lvl {player.level})</div>
              <div style={{ opacity: 0.8 }}>
                Pos: ({Math.floor(player.position.x / 16)}, {Math.floor(player.position.y / 16)}) • HP: {player.hp}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Teleport to Player */}
      <button
        type="button"
        disabled={isBusy || !playerId || !selectedPlayerId}
        onClick={() => {
          const targetPlayer = onlinePlayers.find((p) => p.id === selectedPlayerId);
          if (targetPlayer && playerId) {
            const tileX = Math.floor(targetPlayer.position.x / 16);
            const tileY = Math.floor(targetPlayer.position.y / 16);
            teleportToTile(playerId, tileX, tileY);
          }
        }}
        style={{
          width: '100%',
          padding: '4px',
          fontSize: 10,
          backgroundColor: '#2d3748',
          color: '#edf2f7',
          border: '1px solid #4a5568',
          borderRadius: 0,
          cursor: isBusy || !playerId || !selectedPlayerId ? 'default' : 'pointer',
        }}
      >
        Teleport to This Player
      </button>

       {/* Ship Visual Type Selector */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10 }}>⛵ Ship Visual Type</div>
        
        <div style={{ marginBottom: 4, display: 'flex', gap: 2 }}>
          <select
            value={selectedShipType}
            onChange={(e) => setSelectedShipType(e.target.value)}
            disabled={isBusy}
            style={{
              flex: 1,
              padding: '2px 4px',
              fontSize: 9,
              backgroundColor: '#2d3748',
              color: '#edf2f7',
              border: '1px solid #4a5568',
              borderRadius: 0,
              cursor: isBusy ? 'default' : 'pointer',
            }}
          >
            <option value="sloop">🚤 Sloop (Small)</option>
            <option value="brigantine">⛵ Brigantine (Medium)</option>
            <option value="frigate">🚢 Frigate (Large)</option>
            <option value="galleon">🏴‍☠️ Galleon (Massive)</option>
          </select>
        </div>

        <button
          type="button"
          disabled={isBusy || !selectedPlayerId}
          onClick={() => {
            void callAdminEndpoint('/api/admin/dev/set-ship-type', 'Set ship type', {
              playerId: selectedPlayerId,
              shipType: selectedShipType,
            });
          }}
          style={{
            width: '100%',
            padding: '2px 6px',
            fontSize: 10,
            backgroundColor: '#2d3748',
            color: '#edf2f7',
            border: '1px solid #4a5568',
            borderRadius: 0,
            cursor: isBusy || !selectedPlayerId ? 'default' : 'pointer',
          }}
        >
          Apply Ship Type
        </button>
      </div>

      {/* Respawn Player Button */}
      <button
        type="button"
        disabled={isBusy || !selectedPlayerId}
        onClick={() => {
          void callAdminEndpoint('/api/admin/dev/respawn-player', 'Respawn player', {
            playerId: selectedPlayerId,
          });
        }}
        style={{
          width: '100%',
          padding: '4px 6px',
          fontSize: 10,
          backgroundColor: '#2d3748',
          color: '#edf2f7',
          border: '1px solid #4a5568',
          borderRadius: 0,
          cursor: isBusy || !selectedPlayerId ? 'default' : 'pointer',
        }}
      >
        🔄 Respawn Player
      </button>
    </div>
  );
};
